import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Modal from 'react-modal'; // <-- Ensure react-modal is imported
import { UserProfile, Contact } from '../services/profileService.ts';
import { getMessageHistory, sendMessage as sendApiMessage, uploadImage, Message, markMessagesAsRead, clearConversation, blockUser, unblockUser, checkDetailedBlockStatus } from '../services/chatService.ts';
import DefaultAvatar from '../assets/profil1.jpg';
import SendIcon from '../icons/send.svg';
import ImageIcon from '../icons/image.svg';
import ReplyIcon from '../icons/reply.svg';
import CloseSmallIcon from '../icons/close-small.svg';
import ArrowLeftIcon from '../icons/arrow-left.svg';
import ArrowRightIcon from '../icons/arrow-right.svg';
import CloseIcon from '../icons/close.svg'; // <-- Ensure CloseIcon is imported (for viewer/preview)
import MoreIcon from '../icons/more.svg'; // <-- Need to add this icon
import TrashIcon from '../icons/trash.svg'; // <-- Need to add this icon
import BlockIcon from '../icons/block.svg'; // <-- Need to add this icon
import DonateIcon from '../icons/donate.svg'; // <-- Need to add this icon
import ClipLoader from 'react-spinners/ClipLoader';
import './ChatModal.css';
import { apiUrl } from '../utils/constants.ts';
import { format, isToday, isYesterday, parseISO } from 'date-fns'; // <-- Ensure date-fns functions are imported
import profil1 from '../assets/profil1.jpg'; // <-- Ensure profile pictures are imported
import profil2 from '../assets/profil2.png'; // <-- Ensure profile pictures are imported
import profil3 from '../assets/profil3.png'; // <-- Ensure profile pictures are imported


// Define the structure of messages received/sent via WebSocket (if needed from props)
interface WebSocketMessage {
    type: string;
    payload: any;
}

interface ChatModalProps {
    isOpen: boolean;
    onClose: () => void;
    targetUser: UserProfile | Contact; // User being chatted with
    currentUserAddress: string | null | undefined; // Logged-in user's address
    // --- Add WebSocket props ---
    isConnected: boolean;
    lastMessage: WebSocketMessage | null;
    sendMessage: (message: WebSocketMessage) => void; // Function to send WS message
    // --- End WebSocket props ---
    onMessagesMarkedRead: (senderAddress: string) => void; // Add callback prop
    onChatClose: () => void; // Add callback prop
}

// Helper to format date separators
const formatDateSeparator = (dateString: string): string => {
    const date = parseISO(dateString);
    if (isToday(date)) {
        return 'Today';
    }
    if (isYesterday(date)) {
        return 'Yesterday';
    }
    return format(date, 'MMMM d, yyyy');
};

// Helper to format message time
const formatMessageTime = (dateString: string): string => {
    const date = parseISO(dateString);
    return format(date, 'h:mm a'); // e.g., 3:45 PM
};

// Helper function to get the correct image source
const getProfilePictureSrc = (filename: string | undefined | null): string => {
    // --- MEJORADO: Manejo más robusto de imágenes de perfil ---
    if (!filename) return DefaultAvatar; // Fallback si no hay nombre de archivo
    
    // Verificar si el nombre es una URL completa
    if (filename.startsWith('http')) {
        console.log(`[ChatModal] Usando URL completa para imagen de perfil: ${filename}`);
        return filename;
    }
    
    // Verificar si el nombre es un path relativo que comienza con /
    if (filename.startsWith('/')) {
        const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
        console.log(`[ChatModal] Construyendo URL para path relativo: ${baseUrl}${filename}`);
        return `${baseUrl}${filename}`;
    }
    
    // Manejo específico para los nombres de perfiles predefinidos
    switch (filename) {
        case 'profil1.jpg':
            return profil1;
        case 'profil2.png':
            return profil2;
        case 'profil3.png':
            return profil3;
        default:
            // Si es solo un nombre de archivo sin path, asumimos que está en /token/profile/
            try {
                // Intentar cargar desde archivos estáticos primero (para entorno de desarrollo)
                const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
                console.log(`[ChatModal] Construyendo URL para nombre de archivo: ${baseUrl}/token/profile/${filename}`);
                return `${baseUrl}/token/profile/${filename}`;
            } catch (e) {
                console.warn(`[ChatModal] Error cargando imagen de perfil ${filename}, usando fallback`);
                return DefaultAvatar; // Fallback a DefaultAvatar
            }
    }
};

// --- NEW: Helper to truncate text ---
const truncateText = (text: string, maxLength: number = 50): string => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};
// --- END: Helper ---


const ChatModal: React.FC<ChatModalProps> = ({
    isOpen,
    onClose, // Use onChatClose for custom logic now
    targetUser,
    currentUserAddress,
    isConnected,
    lastMessage,
    sendMessage: sendWsMessage,
    onMessagesMarkedRead, // Destructure callback
    onChatClose, // Destructure callback
}) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false); // <-- Add sending state
    const [error, setError] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null); // Ref to scroll to bottom
    const chatBodyRef = useRef<HTMLDivElement>(null); // <-- Add ref for chat body div
    const fileInputRef = useRef<HTMLInputElement>(null); // Ref for file input

    // --- NEW: State for replying ---
    const [replyingToMessage, setReplyingToMessage] = useState<Message | null>(null);
    // --- END: State for replying ---

    // --- NEW: State for image upload ---
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    // --- END: State for image upload ---

    // --- State for image preview and caption ---
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
    const [imageCaption, setImageCaption] = useState('');
    const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
    // --- END: State for image preview ---

    // --- NEW: State for Image Viewer ---
    const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
    const [imageViewerIndex, setImageViewerIndex] = useState(0);
    // Memoize the list of image messages to avoid recalculating on every render
    const imageMessages = useMemo(() => messages.filter(msg => msg.messageType === 'image' && msg.imageUrl), [messages]);
    // --- END: State for Image Viewer ---

    // --- NEW: State for highlighting replied message ---
    const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
    // --- END NEW ---

    // --- Modified scrollToBottom ---
    // Scrolls to the bottom of the chat container.
    // Use 'instant' for initial load, 'smooth' for new messages might be jarring if user scrolled up.
    const scrollToBottom = (behavior: ScrollBehavior = 'auto') => { // Default to 'auto'
        messagesEndRef.current?.scrollIntoView({ behavior });
    };
    // --- End Modified scrollToBottom ---

    // --- NEW: Check if user is scrolled near the bottom ---
    const isScrolledNearBottom = useCallback(() => {
        const chatBody = chatBodyRef.current;
        if (!chatBody) return true; // Assume near bottom if ref not available yet
        // Check if scroll position is within a threshold (e.g., 50px) of the bottom
        return chatBody.scrollHeight - chatBody.scrollTop - chatBody.clientHeight < 50;
    }, []);
    // --- END NEW ---

    // Añadir una clave para localStorage por cada par de usuarios bloqueados
    const getBlockStorageKey = useCallback((blocker: string, blocked: string) => {
        return `blocked_${blocker.toLowerCase()}_${blocked.toLowerCase()}`;
    }, []);

    // Agregar estado para rastrear si el usuario está bloqueado y en qué dirección
    const [isUserBlocked, setIsUserBlocked] = useState(false);
    const [amIBlocked, setAmIBlocked] = useState(false);

    // Modificar fetchHistory para detectar estados de bloqueo
    const fetchHistory = useCallback(async () => {
        if (!currentUserAddress || !targetUser?.walletAddress) return;
        // --- Add Log ---
        console.log(`[ChatModal fetchHistory] Fetching history for ${targetUser.walletAddress}...`);
        // --- End Log ---
        setIsLoading(true);
        setError(null);
        try {
            const history = await getMessageHistory(currentUserAddress, targetUser.walletAddress);
            
            // --- Add Log ---
            console.log(`[ChatModal fetchHistory] Received ${history.length} messages from service. First message timestamp (if any): ${history[0]?.timestamp}`);
            // --- End Log ---
            
            // NUEVO: Si recibimos 0 mensajes, podría ser porque el usuario está bloqueado
            // Este es un indicador heurístico, no perfecto
            if (history.length === 0) {
                // Comprobar si existe un estado de bloqueo guardado en localStorage
                const blockKey = getBlockStorageKey(currentUserAddress, targetUser.walletAddress);
                const isBlocked = localStorage.getItem(blockKey) === 'true';
                
                if (isBlocked) {
                    console.log(`[ChatModal] User ${targetUser.walletAddress} is blocked according to localStorage`);
                    setIsUserBlocked(true);
                }

                // Comprobar si YO estoy bloqueado por el otro usuario
                const amIBlockedKey = getBlockStorageKey(targetUser.walletAddress, currentUserAddress);
                const blocked = localStorage.getItem(amIBlockedKey) === 'true';
                
                if (blocked) {
                    console.log(`[ChatModal] I am blocked by ${targetUser.walletAddress} according to localStorage`);
                    setAmIBlocked(true);
                    setError("Has sido bloqueado por este usuario. No puedes enviar mensajes.");
                }
            } else {
                // Si hay mensajes, el usuario no está bloqueado
                const blockKey = getBlockStorageKey(currentUserAddress, targetUser.walletAddress);
                localStorage.removeItem(blockKey); // Eliminar cualquier estado de bloqueo guardado
                setIsUserBlocked(false);

                // Comprobar si YO estoy bloqueado por el otro usuario
                const amIBlockedKey = getBlockStorageKey(targetUser.walletAddress, currentUserAddress);
                localStorage.removeItem(amIBlockedKey); // Eliminar estado de bloqueo
                setAmIBlocked(false);
            }
            
            setMessages(history);
            // Mark as read via API and update parent state via callback
            markMessagesAsRead(currentUserAddress, targetUser.walletAddress)
                .then(() => {
                    onMessagesMarkedRead(targetUser.walletAddress); // Notify parent
                })
                .catch(err => console.error("Error marking messages as read on fetch:", err));

            // --- Scroll instantly after history loads ---
            // Use setTimeout to ensure DOM is updated before scrolling
            setTimeout(() => scrollToBottom('instant'), 0);
            // --- End Scroll ---

        } catch (err) {
            // NUEVO: Detectar error 403 (Forbidden) como posible indicador de bloqueo
            if (err instanceof Error && err.message.includes('403')) {
                console.log(`[ChatModal] Received 403 when fetching messages, user might be blocked`);
                
                // Guardar estado de bloqueo en localStorage
                const blockKey = getBlockStorageKey(currentUserAddress, targetUser.walletAddress);
                localStorage.setItem(blockKey, 'true');
                setIsUserBlocked(true);

                // Probablemente YO estoy bloqueado por el otro usuario
                const amIBlockedKey = getBlockStorageKey(targetUser.walletAddress, currentUserAddress);
                localStorage.setItem(amIBlockedKey, 'true');
                setAmIBlocked(true);
                setError("Has sido bloqueado por este usuario. No puedes enviar mensajes.");
            }
            
            console.error("Failed to fetch message history:", err);
            setError(prevError => prevError || "Failed to load messages.");
            setMessages([]); // Clear messages on error
        } finally {
            setIsLoading(false);
        }
    }, [currentUserAddress, targetUser?.walletAddress, onMessagesMarkedRead, getBlockStorageKey]); // Add getBlockStorageKey to dependencies

    // Effect to fetch history when modal opens or target user changes
    useEffect(() => {
        if (isOpen) {
            fetchHistory();
        } else {
            // Reset state when modal closes
            setMessages([]);
            setNewMessage('');
            setError(null);
            setReplyingToMessage(null); // <-- Reset reply state
            setUploadError(null); // <-- Reset upload error
            // --- Reset image preview state ---
            setImagePreviewUrl(null);
            setImageCaption('');
            setSelectedImageFile(null);
            // --- End reset ---
        }
    }, [isOpen, fetchHistory]);

    // Effect to handle incoming WebSocket messages (uses props now)
    useEffect(() => {
        if (isOpen && lastMessage && lastMessage.type === 'NEW_MESSAGE') { // Check isOpen here too
            const receivedMessage: Message = lastMessage.payload;
            // Add message only if it involves the current chat participants
            const involvesCurrentChat =
                (receivedMessage.senderAddress.toLowerCase() === currentUserAddress?.toLowerCase() && receivedMessage.receiverAddress.toLowerCase() === targetUser.walletAddress.toLowerCase()) ||
                (receivedMessage.senderAddress.toLowerCase() === targetUser.walletAddress.toLowerCase() && receivedMessage.receiverAddress.toLowerCase() === currentUserAddress?.toLowerCase());

            if (involvesCurrentChat) {
                // --- Check scroll position BEFORE adding message ---
                const shouldScroll = isScrolledNearBottom();
                // --- End Check ---

                // Avoid adding duplicates if message was already added optimistically
                setMessages((prevMessages) => {
                    // Check if message with same _id OR same sender/timestamp already exists
                    if (!prevMessages.some(msg => msg._id === receivedMessage._id || (msg.senderAddress === receivedMessage.senderAddress && msg.timestamp === receivedMessage.timestamp && msg.content === receivedMessage.content))) {
                        return [...prevMessages, receivedMessage];
                    }
                    return prevMessages;
                });

                // --- Scroll only if user was near bottom ---
                if (shouldScroll) {
                    // Use timeout to allow DOM update before scrolling
                    setTimeout(() => scrollToBottom('smooth'), 0);
                }
                // --- End Scroll ---
            }
        }
        // Depend on isOpen, lastMessage, currentUserAddress, targetUser, sendWsMessage
    }, [isOpen, lastMessage, currentUserAddress, targetUser?.walletAddress, sendWsMessage, onMessagesMarkedRead, isScrolledNearBottom]); // Add callback to dependencies

    // --- LOG isConnected PROP ---
    useEffect(() => {
        console.log(`[ChatModal] isConnected prop updated: ${isConnected}`);
    }, [isConnected]);
    // --- END LOG ---

    // --- MODIFIED: Handle sending TEXT message ---
    const handleSendTextMessage = async () => {
        const contentToSend = newMessage.trim();
        if (!contentToSend || !currentUserAddress || !targetUser?.walletAddress || isSending || isUploading) return;

        // No permitir enviar mensajes si estoy bloqueado - STRONGER CHECK
        if (amIBlocked) {
            console.log("[ChatModal] Attempted to send message while blocked. Blocking operation.");
            setError("Has sido bloqueado por este usuario. No puedes enviar mensajes.");
            setNewMessage(''); // Clear the input field to prevent repeated attempts
            return; // IMPORTANT: Return early to prevent message sending
        }

        // Additional check against localStorage in case state gets out of sync
        const amIBlockedKey = getBlockStorageKey(targetUser.walletAddress, currentUserAddress);
        if (localStorage.getItem(amIBlockedKey) === 'true') {
            console.log("[ChatModal] Blocked status detected in localStorage. Blocking message send.");
            setAmIBlocked(true); // Update state to match localStorage
            setError("Has sido bloqueado por este usuario. No puedes enviar mensajes.");
            setNewMessage('');
            return;
        }

        setIsSending(true);
        setError(null);

        const optimisticTimestamp = new Date().toISOString();
        // --- Generate a temporary unique ID for optimistic message ---
        const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        // --- End ---
        const optimisticMessage: Message = {
            _id: optimisticId, // Use temporary ID
            senderAddress: currentUserAddress,
            receiverAddress: targetUser.walletAddress,
            messageType: 'text',
            content: contentToSend,
            replyToMessageId: replyingToMessage?._id || null,
            replyToMessage: replyingToMessage ? {
                content: replyingToMessage.content,
                senderAddress: replyingToMessage.senderAddress,
                messageType: replyingToMessage.messageType,
                imageUrl: replyingToMessage.imageUrl,
            } : null,
            timestamp: optimisticTimestamp,
            read: false,
        };

        // --- Add Log ---
        console.log('[ChatModal handleSendTextMessage] Adding optimistic message:', optimisticMessage);
        // --- End Log ---

        const shouldScroll = isScrolledNearBottom();

        setMessages(prevMessages => [...prevMessages, optimisticMessage]);
        setNewMessage('');
        setReplyingToMessage(null);

        if (shouldScroll) {
             setTimeout(() => scrollToBottom('smooth'), 0);
        }

        try {
            const savedMessage = await sendApiMessage(
                currentUserAddress,
                targetUser.walletAddress,
                'text',
                contentToSend,
                replyingToMessage?._id
            );

            // --- Add Log ---
            console.log('[ChatModal handleSendTextMessage] Received saved message from API:', savedMessage);
            // --- End Log ---

            setMessages(prevMessages => {
                // --- Add Log ---
                console.log(`[ChatModal handleSendTextMessage] Updating state. Replacing optimistic ID ${optimisticId} with real ID ${savedMessage._id}. Previous state length: ${prevMessages.length}`);
                // --- End Log ---
                const updatedMessages = prevMessages.map(msg =>
                    (msg._id === optimisticId) // Match using the temporary ID
                    ? { ...savedMessage, _id: savedMessage._id } // Replace with saved message
                    : msg
                );
                // --- Add Log ---
                console.log(`[ChatModal handleSendTextMessage] New state length: ${updatedMessages.length}. Message with ID ${savedMessage._id} exists: ${updatedMessages.some(m => m._id === savedMessage._id)}`);
                // --- End Log ---
                return updatedMessages;
            });


        } catch (err) {
            console.error("Failed to send text message:", err);
            setError("Failed to send message.");
            // --- Update removal logic to use optimisticId ---
            setMessages(prevMessages => prevMessages.filter(msg => msg._id !== optimisticId));
            // --- End ---
            setNewMessage(contentToSend);
        } finally {
            setIsSending(false);
        }
    };
    // --- END: Text Send Handler ---

    // --- MODIFIED: Handle sending IMAGE message (called from preview) ---
    const handleSendImageWithCaption = async () => {
        if (!selectedImageFile || !currentUserAddress || !targetUser?.walletAddress || isUploading || isSending) return;

        // No permitir enviar imágenes si estoy bloqueado - STRONGER CHECK
        if (amIBlocked) {
            console.log("[ChatModal] Attempted to send image while blocked. Blocking operation.");
            setError("Has sido bloqueado por este usuario. No puedes enviar mensajes.");
            handleCancelImagePreview(); // Close the preview
            return; // IMPORTANT: Return early to prevent image sending
        }

        // Additional check against localStorage in case state gets out of sync
        const amIBlockedKey = getBlockStorageKey(targetUser.walletAddress, currentUserAddress);
        if (localStorage.getItem(amIBlockedKey) === 'true') {
            console.log("[ChatModal] Blocked status detected in localStorage. Blocking image send.");
            setAmIBlocked(true); // Update state to match localStorage
            setError("Has sido bloqueado por este usuario. No puedes enviar mensajes.");
            handleCancelImagePreview();
            return;
        }

        setIsUploading(true); // Use uploading state for the whole process
        setIsSending(true); // Also set sending state
        setUploadError(null);
        setError(null);

        const captionToSend = imageCaption.trim(); // Use the state variable
        const optimisticTimestamp = new Date().toISOString();
        // --- Generate a temporary unique ID for optimistic message ---
        const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        // --- End ---
        let uploadedImageUrl: string | null = null; // Variable to store the URL

        // --- Log entry ---
        console.log(`[ChatModal] handleSendImageWithCaption: Starting upload for file: ${selectedImageFile.name}`);
        // --- ---

        try {
            // 1. Upload Image
            const uploadResult = await uploadImage(selectedImageFile, currentUserAddress);
            if (!uploadResult.success || !uploadResult.imageUrl) {
                throw new Error(uploadResult.error || "Image upload failed.");
            }
            uploadedImageUrl = uploadResult.imageUrl; // Store the URL
            // --- Log upload success ---
            console.log(`[ChatModal] handleSendImageWithCaption: Upload successful. URL: ${uploadedImageUrl}`);
            // --- ---


            const shouldScroll = isScrolledNearBottom();

            // 2. Optimistic UI update (after successful upload)
            const optimisticMessage: Message = {
                _id: optimisticId, // Use temporary ID
                senderAddress: currentUserAddress,
                receiverAddress: targetUser.walletAddress,
                messageType: 'image',
                imageUrl: uploadedImageUrl, // Use the actual URL from upload
                content: captionToSend || undefined, // Caption
                replyToMessageId: replyingToMessage?._id || null,
                replyToMessage: replyingToMessage ? {
                    content: replyingToMessage.content,
                    senderAddress: replyingToMessage.senderAddress,
                    messageType: replyingToMessage.messageType,
                    imageUrl: replyingToMessage.imageUrl,
                } : null,
                timestamp: optimisticTimestamp, // Use the timestamp generated before API calls
                read: false,
                // No _id for optimistic message
            };

            // --- Log before optimistic update ---
            console.log('[ChatModal] handleSendImageWithCaption: Adding optimistic message:', optimisticMessage);
            // --- ---
            setMessages(prevMessages => {
                const updatedMessages = [...prevMessages, optimisticMessage];
                // --- Log after optimistic update (inside setter) ---
                console.log('[ChatModal] handleSendImageWithCaption: State AFTER optimistic add:', updatedMessages);
                // --- ---
                return updatedMessages;
            });


            if (shouldScroll) {
                 setTimeout(() => scrollToBottom('smooth'), 0);
            }

            // Clear preview and input states *after* adding optimistic message
            setImagePreviewUrl(null);
            setImageCaption('');
            setSelectedImageFile(null);
            setReplyingToMessage(null);

            // 3. Send message via API (with image URL and caption)
            // --- Log before sending message details ---
            console.log(`[ChatModal] handleSendImageWithCaption: Sending message details to API. ImageUrl: ${uploadedImageUrl}, Caption: ${captionToSend}, ReplyTo: ${replyingToMessage?._id}`);
            // --- ---
            const savedMessage = await sendApiMessage(
                currentUserAddress,
                targetUser.walletAddress,
                'image',
                uploadedImageUrl, // Pass imageUrl as the 4th argument
                replyingToMessage?._id,
                captionToSend // Pass captionToSend as the 6th argument
            );
            // --- Log API response ---
            console.log('[ChatModal] handleSendImageWithCaption: Received saved message from API:', savedMessage);
            // --- ---

            // 4. Update optimistic message with real data
            setMessages(prevMessages => {
                // --- Log before final update ---
                console.log(`[ChatModal] handleSendImageWithCaption: Attempting to replace optimistic message (timestamp: ${optimisticTimestamp}) with saved message (ID: ${savedMessage._id})`);
                // --- ---
                const finalMessages = prevMessages.map(msg =>
                    // Match based on the timestamp AND sender AND lack of _id (more robust for optimistic)
                    (msg.timestamp === optimisticTimestamp && msg.senderAddress === currentUserAddress && !msg._id)
                    ? { ...savedMessage, _id: savedMessage._id } // Replace with saved message, ensuring _id is set
                    : msg
                );
                // --- Log after final update (inside setter) ---
                console.log('[ChatModal] handleSendImageWithCaption: State AFTER final update:', finalMessages);
                // --- ---
                // Check if the message was actually updated
                if (!finalMessages.some(msg => msg._id === savedMessage._id)) {
                    console.warn(`[ChatModal] handleSendImageWithCaption: Failed to find and replace optimistic message for timestamp ${optimisticTimestamp}. Saved message might be duplicated or missing.`);
                }
                // --- ---
                return finalMessages;
            });

        } catch (err) {
            // ... existing error handling ...
            console.error("Failed to send image message:", err);
            const errorMsg = err instanceof Error ? err.message : "Failed to send image.";
            setError(errorMsg);
            setUploadError(errorMsg);

            // --- Attempt to remove optimistic message on failure ---
            if (uploadedImageUrl) { // Only remove if upload succeeded but send failed
                 console.log(`[ChatModal] handleSendImageWithCaption: Error occurred after upload. Removing optimistic message with timestamp ${optimisticTimestamp}`);
                 setMessages(prevMessages => prevMessages.filter(msg =>
                     !(msg.timestamp === optimisticTimestamp && msg.senderAddress === currentUserAddress && !msg._id)
                 ));
            }
            // --- ---

        } finally {
            setIsUploading(false);
            setIsSending(false);
            // --- Log completion ---
            console.log('[ChatModal] handleSendImageWithCaption: Process finished.');
            // --- ---
        }
    };
    // --- END: Image Send Handler ---


    // --- MODIFIED: Handle Image Selection ---
    const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !currentUserAddress) return;

        // Basic validation (optional)
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setUploadError("Invalid file type. Please select a JPG, PNG, GIF, or WEBP image.");
            return;
        }
        if (file.size > 10 * 1024 * 1024) { // 10MB limit example
             setUploadError("File is too large. Maximum size is 10MB.");
             return;
        }


        setSelectedImageFile(file);
        setImagePreviewUrl(URL.createObjectURL(file));
        setUploadError(null); // Clear previous errors
        setImageCaption(''); // Reset caption

        // Reset file input value so the same file can be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const triggerImageUpload = () => {
        // Close preview if open, before triggering new selection
        handleCancelImagePreview();
        fileInputRef.current?.click();
    };

    // --- NEW: Handle cancelling image preview ---
    const handleCancelImagePreview = () => {
        if (imagePreviewUrl) {
            URL.revokeObjectURL(imagePreviewUrl); // Clean up object URL
        }
        setImagePreviewUrl(null);
        setSelectedImageFile(null);
        setImageCaption('');
        setUploadError(null);
    };
    // --- END: Image Preview Handlers ---


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewMessage(e.target.value);
    };

    const handleCaptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setImageCaption(e.target.value);
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !e.shiftKey && !isSending && !isUploading && !imagePreviewUrl) { // Only send text if no preview
            e.preventDefault();
            handleSendTextMessage();
        }
    };

    const handleCaptionKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
         if (e.key === 'Enter' && !e.shiftKey && !isSending && !isUploading) {
             e.preventDefault();
             handleSendImageWithCaption();
         }
     };

    const handleStartReply = (message: Message) => {
        setReplyingToMessage(message);
    };

    const handleCancelReply = () => {
        setReplyingToMessage(null);
    };

    // --- NEW: Image Viewer Handlers ---
    const openImageViewer = (clickedImageUrl: string) => {
        const index = imageMessages.findIndex(msg => msg.imageUrl === clickedImageUrl);
        if (index !== -1) {
            setImageViewerIndex(index);
            setIsImageViewerOpen(true);
        }
    };

    const closeImageViewer = () => {
        setIsImageViewerOpen(false);
    };

    const showNextImage = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent closing modal when clicking buttons
        setImageViewerIndex((prevIndex) => (prevIndex + 1) % imageMessages.length);
    };

    const showPrevImage = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent closing modal when clicking buttons
        setImageViewerIndex((prevIndex) => (prevIndex - 1 + imageMessages.length) % imageMessages.length);
    };
    // --- END: Image Viewer Handlers ---

    // --- NEW: Handler for clicking reply context ---
    const handleReplyContextClick = (originalMessageId: string | undefined) => {
        if (!originalMessageId) return;

        const element = document.getElementById(`message-${originalMessageId}`);
        if (element) {
            console.log(`Scrolling to message-${originalMessageId}`);
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Highlight the message
            setHighlightedMessageId(originalMessageId);

            // Remove highlight after a delay
            setTimeout(() => {
                setHighlightedMessageId(null);
            }, 1500); // Highlight for 1.5 seconds
        } else {
            console.warn(`Could not find element for message-${originalMessageId}`);
            // Optionally load older messages if the element isn't found? (More complex)
        }
    };
    // --- END NEW ---

    // Group messages by date
    const groupedMessages: { date: string; messages: Message[] }[] = [];
    let lastDate: string | null = null;

    // --- FIX: Ensure messages array is valid before processing ---
    if (Array.isArray(messages)) {
        messages.forEach(message => {
            // --- FIX: Add check for valid timestamp ---
            if (message && message.timestamp) {
                try {
                    const messageDate = format(parseISO(message.timestamp), 'yyyy-MM-dd');
                    if (messageDate !== lastDate) {
                        groupedMessages.push({ date: messageDate, messages: [message] });
                        lastDate = messageDate;
                    } else {
                        // Ensure the last group exists before pushing
                        if (groupedMessages.length > 0) {
                            groupedMessages[groupedMessages.length - 1].messages.push(message);
                        } else {
                             // This case should ideally not happen if logic is correct, but handles edge case
                             groupedMessages.push({ date: messageDate, messages: [message] });
                             lastDate = messageDate;
                        }
                    }
                } catch (e) {
                    console.error("Error parsing message timestamp during grouping:", message.timestamp, e);
                    // Optionally handle messages with invalid timestamps (e.g., skip or group under 'invalid-date')
                }
            } else {
                 console.warn("Skipping message with missing or invalid timestamp:", message);
            }
            // --- END FIX ---
        });
    } else {
        console.error("Messages state is not an array:", messages);
    }
    // --- END FIX ---


    // Agregar estados para el menú desplegable
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isConfirmingAction, setIsConfirmingAction] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Agregar estado para rastrear si el usuario está bloqueado
    
    // Agregar useEffect para consultar el estado de bloqueo
    useEffect(() => {
        // Solo verificar si hay un usuario objetivo y el modal está abierto
        if (targetUser?.walletAddress && currentUserAddress && isOpen) {
            // En lugar de hacer una solicitud a un endpoint que no existe,
            // simplemente asumimos que el usuario no está bloqueado
            setIsUserBlocked(false);
            
            // O puedes verificar el estado de bloqueo usando la API existente (opción más segura)
            // Ejemplo comentado:
            /*
            fetch(`${apiUrl}/api/chat/messages/${targetUser.walletAddress}?walletAddress=${currentUserAddress}`)
                .then(response => response.json())
                .then(data => {
                    // Aquí podrías examinar la respuesta de los mensajes para determinar
                    // si el usuario está bloqueado, por ejemplo, si hay un campo isBlocked
                    setIsUserBlocked(false); // Por defecto, asumimos que no está bloqueado
                })
                .catch(err => {
                    console.error("Error checking messages:", err);
                    setIsUserBlocked(false); // Por defecto, asumimos que no está bloqueado en caso de error
                });
            */
        }
    }, [targetUser, currentUserAddress, isOpen, apiUrl]);

    // Añadir manejador para WebSocket de bloqueo/desbloqueo
    useEffect(() => {
        if (isOpen && lastMessage) {
            if (lastMessage.type === 'USER_BLOCKED' && 
                lastMessage.payload?.blockedUser?.toLowerCase() === targetUser?.walletAddress?.toLowerCase()) {
                // Actualizar estado local cuando se recibe mensaje de bloqueo
                setIsUserBlocked(true);
            } else if (lastMessage.type === 'USER_UNBLOCKED' && 
                     lastMessage.payload?.unblockedUser?.toLowerCase() === targetUser?.walletAddress?.toLowerCase()) {
                // Actualizar estado local cuando se recibe mensaje de desbloqueo
                setIsUserBlocked(false);
            }
        }
    }, [lastMessage, targetUser, isOpen]);

    // Agregar función para manejar clic fuera del menú
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuOpen(false);
            }
        }

        // Agregar event listener cuando el menú está abierto
        if (isMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        // Limpiar event listener
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMenuOpen]);

    // Función para limpiar la conversación
    const handleClearChat = async () => {
        if (!targetUser?.walletAddress || !currentUserAddress) return;
        
        setIsConfirmingAction('clear');
        
        if (window.confirm('¿Estás seguro que deseas eliminar todos los mensajes? Esta acción no se puede deshacer.')) {
            try {
                // Intentar borrar conversación varias veces si falla
                let attempts = 0;
                let success = false;
                let result;
                
                while (attempts < 3 && !success) {
                    attempts++;
                    try {
                        result = await clearConversation(targetUser.walletAddress, currentUserAddress);
                        if (result.success && (result.deletedCount === undefined || result.deletedCount > 0)) {
                            success = true;
                        } else if (!result.success) {
                            console.warn(`[ChatModal] Clear conversation attempt ${attempts} failed: ${result.error}`);
                            await new Promise(resolve => setTimeout(resolve, 500)); // Pequeña pausa entre intentos
                        } else {
                            console.log(`[ChatModal] Clear conversation returned success but deletedCount=${result.deletedCount}`);
                            success = true; // Consideramos éxito incluso si deletedCount es 0
                        }
                    } catch (err) {
                        console.error(`[ChatModal] Clear conversation attempt ${attempts} error:`, err);
                        await new Promise(resolve => setTimeout(resolve, 500));
                    }
                }
                
                if (success) {
                    // Actualizar estado local para reflejar la conversación vacía
                    setMessages([]);
                    setIsMenuOpen(false);
                    
                    // Enviar mensaje WebSocket para notificar al otro usuario
                    if (sendWsMessage) {
                        sendWsMessage({
                            type: 'CONVERSATION_CLEARED',
                            payload: {
                                otherUserAddress: targetUser.walletAddress
                            }
                        });
                    }
                } else {
                    setError(result?.error || 'No se pudo limpiar la conversación después de varios intentos');
                }
            } catch (err) {
                console.error('Error al limpiar la conversación:', err);
                setError('Error al limpiar la conversación');
            } finally {
                setIsConfirmingAction(null);
            }
        } else {
            setIsConfirmingAction(null);
        }
    };

    // Función para bloquear/desbloquear usuario
    const handleBlockUser = async () => {
        if (!targetUser?.walletAddress || !currentUserAddress) return;
        
        setIsConfirmingAction('block');
        
        // Si ya está bloqueado, desbloquear
        if (isUserBlocked) {
            if (window.confirm('¿Estás seguro que deseas desbloquear a este usuario? Podrán enviarte mensajes nuevamente.')) {
                try {
                    const result = await unblockUser(targetUser.walletAddress, currentUserAddress);
                    
                    if (result.success) {
                        setIsMenuOpen(false);
                        setIsUserBlocked(false); // Actualizar estado local
                        
                        // NUEVO: Eliminar entrada de localStorage
                        const blockKey = getBlockStorageKey(currentUserAddress, targetUser.walletAddress);
                        localStorage.removeItem(blockKey);
                        
                        alert('Usuario desbloqueado con éxito.');
                        
                        // Notificar vía WebSocket
                        if (sendWsMessage) {
                            sendWsMessage({
                                type: 'USER_UNBLOCKED',
                                payload: {
                                    unblockedUser: targetUser.walletAddress
                                }
                            });
                        }
                    } else {
                        setError(result.error || 'No se pudo desbloquear al usuario');
                    }
                } catch (err) {
                    console.error('Error al desbloquear usuario:', err);
                    setError('Error al desbloquear usuario');
                } finally {
                    setIsConfirmingAction(null);
                }
            } else {
                setIsConfirmingAction(null);
            }
        } else {
            // Lógica existente de bloqueo
            if (window.confirm('¿Estás seguro que deseas bloquear a este usuario? No podrán enviarte mensajes.')) {
                try {
                    const result = await blockUser(targetUser.walletAddress, currentUserAddress);
                    
                    if (result.success) {
                        setIsMenuOpen(false);
                        setIsUserBlocked(true); // Actualizar estado local
                        
                        // NUEVO: Guardar estado en localStorage
                        const blockKey = getBlockStorageKey(currentUserAddress, targetUser.walletAddress);
                        localStorage.setItem(blockKey, 'true');
                        
                        // Notificar vía WebSocket
                        if (sendWsMessage) {
                            sendWsMessage({
                                type: 'USER_BLOCKED',
                                payload: {
                                    blockedUser: targetUser.walletAddress
                                }
                            });
                        }
                        
                        alert('Usuario bloqueado con éxito.');
                        onChatClose(); // Cerrar el chat después de bloquear
                    } else {
                        setError(result.error || 'No se pudo bloquear al usuario');
                    }
                } catch (err) {
                    console.error('Error al bloquear usuario:', err);
                    setError('Error al bloquear usuario');
                } finally {
                    setIsConfirmingAction(null);
                }
            } else {
                setIsConfirmingAction(null);
            }
        }
    };

    // Adicionar un efecto para manejar eventos WebSocket de bloqueo/desbloqueo
    useEffect(() => {
        if (isOpen && lastMessage) {
            // Manejar mensaje USER_BLOCKED
            if (lastMessage.type === 'USER_BLOCKED') {
                const { blockedUser, blockerUser } = lastMessage.payload || {};
                
                // Caso 1: Fui yo quien bloqueó a otro usuario
                if (blockerUser?.toLowerCase() === currentUserAddress?.toLowerCase() && 
                    blockedUser?.toLowerCase() === targetUser?.walletAddress?.toLowerCase()) {
                    setIsUserBlocked(true);
                    const blockKey = getBlockStorageKey(currentUserAddress, targetUser.walletAddress);
                    localStorage.setItem(blockKey, 'true');
                }
                
                // Caso 2: Fui yo el bloqueado
                if (blockedUser?.toLowerCase() === currentUserAddress?.toLowerCase() && 
                    blockerUser?.toLowerCase() === targetUser?.walletAddress?.toLowerCase()) {
                    // Mostrar notificación a usuario
                    setError("Has sido bloqueado por este usuario");
                }
            }
            
            // Manejar mensaje USER_UNBLOCKED
            if (lastMessage.type === 'USER_UNBLOCKED') {
                const { unblockedUser, unblockerUser } = lastMessage.payload || {};
                
                // Caso 1: Fui yo quien desbloqueó a otro usuario
                if (unblockerUser?.toLowerCase() === currentUserAddress?.toLowerCase() && 
                    unblockedUser?.toLowerCase() === targetUser?.walletAddress?.toLowerCase()) {
                    setIsUserBlocked(false);
                    const blockKey = getBlockStorageKey(currentUserAddress, targetUser.walletAddress);
                    localStorage.removeItem(blockKey);
                }
                
                // Caso 2: Fui yo el desbloqueado
                if (unblockedUser?.toLowerCase() === currentUserAddress?.toLowerCase() && 
                    unblockerUser?.toLowerCase() === targetUser?.walletAddress?.toLowerCase()) {
                    // Volver a cargar mensajes
                    fetchHistory();
                }
            }
        }
    }, [lastMessage, targetUser, isOpen, currentUserAddress, getBlockStorageKey, fetchHistory]);

    // Modificar el manejador para websocket para USER_BLOCKED
    useEffect(() => {
        if (isOpen && lastMessage) {
            // Manejar mensaje USER_BLOCKED
            if (lastMessage.type === 'USER_BLOCKED') {
                // Extraer información del mensaje - manejar diferentes formatos de payload
                const blockedUser = lastMessage.payload?.blockedUser || lastMessage.payload?.blockedAddress;
                const blockerUser = lastMessage.payload?.blockerUser || lastMessage.payload?.blockerAddress;
                
                console.log(`[ChatModal] Received USER_BLOCKED - blocker: ${blockerUser}, blocked: ${blockedUser}`);
                
                if (!blockedUser || !blockerUser) {
                    console.error("[ChatModal] Invalid USER_BLOCKED payload:", lastMessage.payload);
                    return;
                }
                
                // Caso 1: Yo bloqueé a otro usuario
                if (blockerUser.toLowerCase() === currentUserAddress?.toLowerCase() && 
                    blockedUser.toLowerCase() === targetUser?.walletAddress?.toLowerCase()) {
                    console.log(`[ChatModal] I blocked ${targetUser.walletAddress}`);
                    setIsUserBlocked(true);
                    const blockKey = getBlockStorageKey(currentUserAddress, targetUser.walletAddress);
                    localStorage.setItem(blockKey, 'true');
                }
                
                // Caso 2: Fui bloqueado por el otro usuario
                if (blockedUser.toLowerCase() === currentUserAddress?.toLowerCase() && 
                    blockerUser.toLowerCase() === targetUser?.walletAddress?.toLowerCase()) {
                    console.log(`[ChatModal] I was blocked by ${targetUser.walletAddress}`);
                    setAmIBlocked(true);
                    setError("Has sido bloqueado por este usuario. No puedes enviar mensajes.");
                    
                    // Guardar estado en localStorage
                    const amIBlockedKey = getBlockStorageKey(targetUser.walletAddress, currentUserAddress);
                    localStorage.setItem(amIBlockedKey, 'true');
                }
            }
            
            // Manejar mensaje USER_UNBLOCKED
            if (lastMessage.type === 'USER_UNBLOCKED') {
                const unblockedUser = lastMessage.payload?.unblockedUser || lastMessage.payload?.unblockedAddress;
                const unblockerUser = lastMessage.payload?.unblockerUser || lastMessage.payload?.unblockerAddress;
                
                console.log(`[ChatModal] Received USER_UNBLOCKED - unblocker: ${unblockerUser}, unblocked: ${unblockedUser}`);
                
                if (!unblockedUser || !unblockerUser) {
                    console.error("[ChatModal] Invalid USER_UNBLOCKED payload:", lastMessage.payload);
                    return;
                }
                
                // Caso 1: Yo desbloqueé a otro usuario
                if (unblockerUser.toLowerCase() === currentUserAddress?.toLowerCase() && 
                    unblockedUser.toLowerCase() === targetUser?.walletAddress?.toLowerCase()) {
                    console.log(`[ChatModal] I unblocked ${targetUser.walletAddress}`);
                    setIsUserBlocked(false);
                    const blockKey = getBlockStorageKey(currentUserAddress, targetUser.walletAddress);
                    localStorage.removeItem(blockKey);
                }
                
                // Caso 2: Fui desbloqueado por el otro usuario
                if (unblockedUser.toLowerCase() === currentUserAddress?.toLowerCase() && 
                    unblockerUser.toLowerCase() === targetUser?.walletAddress?.toLowerCase()) {
                    console.log(`[ChatModal] I was unblocked by ${targetUser.walletAddress}`);
                    setAmIBlocked(false);
                    setError(null);
                    
                    // Eliminar estado de localStorage
                    const amIBlockedKey = getBlockStorageKey(targetUser.walletAddress, currentUserAddress);
                    localStorage.removeItem(amIBlockedKey);
                    
                    // Volver a cargar mensajes
                    fetchHistory();
                }
            }
        }
    }, [lastMessage, targetUser, isOpen, currentUserAddress, getBlockStorageKey, fetchHistory]);

    // Función para manejar donación (placeholder para implementación futura)
    const handleDonate = () => {
        setIsMenuOpen(false);
        alert('Función de donación estará disponible próximamente.');
    };

    // Update useEffect to properly refresh block status and prevent input submission
    useEffect(() => {
        if (isOpen && currentUserAddress && targetUser?.walletAddress) {
            // Check both localStorage and fetch fresh block status when chat opens
            const refreshBlockStatus = async () => {
                // Check localStorage first for immediate UI feedback
                const amIBlockedKey = getBlockStorageKey(targetUser.walletAddress, currentUserAddress);
                const storedBlockStatus = localStorage.getItem(amIBlockedKey) === 'true';
                
                if (storedBlockStatus) {
                    console.log(`[ChatModal] I am blocked by ${targetUser.walletAddress} according to localStorage`);
                    setAmIBlocked(true);
                    setError("Has sido bloqueado por este usuario. No puedes enviar mensajes.");
                }
                
                try {
                    // FIX: Use the correct endpoint path
                    const response = await fetch(`${apiUrl}/api/chat/block/status/${targetUser.walletAddress}?walletAddress=${currentUserAddress}`);
                    
                    // Handle non-JSON responses
                    if (!response.ok) {
                        console.error(`[ChatModal] Error checking block status: HTTP ${response.status}`);
                        // Keep whatever state we got from localStorage if server check fails
                        return;
                    }
                    
                    const data = await response.json();
                    
                    if (data.isBlocked) {
                        console.log(`[ChatModal] Server confirms I am blocked by ${targetUser.walletAddress}`);
                        setAmIBlocked(true);
                        setError("Has sido bloqueado por este usuario. No puedes enviar mensajes.");
                        
                        // Update localStorage
                        localStorage.setItem(amIBlockedKey, 'true');
                    } else if (storedBlockStatus) {
                        // If localStorage said blocked but server says not, update localStorage
                        localStorage.removeItem(amIBlockedKey);
                        setAmIBlocked(false);
                        setError(null);
                    }
                } catch (err) {
                    console.error("Error checking block status:", err);
                    // Keep whatever state we got from localStorage if server check fails
                }
            };
            
            refreshBlockStatus();
        }
    }, [isOpen, currentUserAddress, targetUser?.walletAddress, getBlockStorageKey]);

    // Add enhanced block status check and UI to show if you've been blocked or have blocked someone
    useEffect(() => {
        // Only check if there is a user target and the modal is open
        if (targetUser?.walletAddress && currentUserAddress && isOpen) {
          // Use enhanced block status check for more details
          const checkBlockStatus = async () => {
            try {
              console.log('[ChatModal] Checking detailed block status between current user and target');
              const result = await checkDetailedBlockStatus(
                targetUser.walletAddress,
                currentUserAddress
              );
              
              if (result.success) {
                // Enhanced logging
                console.log('[ChatModal] Block status check result:', {
                  iBlockedThem: result.isBlocked,
                  theyBlockedMe: result.amIBlocked
                });
                
                // Update both block states
                setIsUserBlocked(result.isBlocked);
                setAmIBlocked(result.amIBlocked);
                
                // Store in localStorage for persistence
                const meBlockingThemKey = getBlockStorageKey(currentUserAddress, targetUser.walletAddress);
                const themBlockingMeKey = getBlockStorageKey(targetUser.walletAddress, currentUserAddress);
                
                localStorage.setItem(meBlockingThemKey, result.isBlocked.toString());
                localStorage.setItem(themBlockingMeKey, result.amIBlocked.toString());
              } else {
                console.error('[ChatModal] Failed to check block status:', result.error);
              }
            } catch (error) {
              console.error('[ChatModal] Error checking block status:', error);
            }
          };
          
          checkBlockStatus();
        }
    }, [targetUser, currentUserAddress, isOpen, getBlockStorageKey]);

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onChatClose} // Use the callback prop
            className="Modal_chat" // Specific class for chat modal styling
            overlayClassName="Overlay"
            ariaHideApp={false} // Development only, set app element properly in production
        > {/* <-- Correct: Keep Modal open here */}

            {/* All modal content should be inside the Modal component */}
            <div className="chat-header">
                <img
                    // Use the helper function to get the correct source
                    src={getProfilePictureSrc(targetUser?.profilePicture)}
                    alt={targetUser?.username || 'User'}
                    className="chat-avatar"
                />
                <span className="chat-username">{targetUser?.username || 'User...'}</span>
                
                {/* Botón de menú de tres puntos */}
                <div ref={menuRef} style={{ position: 'relative' }}>
                    <button 
                        onClick={() => setIsMenuOpen(!isMenuOpen)} 
                        className="chat-menu-button" 
                        title="Opciones"
                    >
                        <img src={MoreIcon} alt="Más opciones" />
                    </button>
                    
                    {/* Menú desplegable */}
                    {isMenuOpen && (
                        <div className="chat-dropdown-menu">
                            <div 
                                className="menu-item"
                                onClick={handleClearChat}
                            >
                                <img src={TrashIcon} alt="Limpiar" />
                                {isConfirmingAction === 'clear' ? 'Procesando...' : 'Limpiar chat'}
                            </div>
                            <div className="menu-divider"></div>
                            <div 
                                className={`menu-item ${isUserBlocked ? "" : "danger"}`}
                                onClick={handleBlockUser}
                            >
                                <img src={isUserBlocked ? BlockIcon : BlockIcon} alt={isUserBlocked ? "Desbloquear" : "Bloquear"} />
                                {isConfirmingAction === 'block' ? 'Procesando...' : isUserBlocked ? 'Desbloquear usuario' : 'Bloquear usuario'}
                            </div>
                            <div className="menu-divider"></div>
                            <div 
                                className="menu-item"
                                onClick={handleDonate}
                            >
                                <img src={DonateIcon} alt="Donar" />
                                Donar
                            </div>
                        </div>
                    )}
                </div>
                
                <button onClick={onChatClose} className="chat-close-button"> {/* Use callback */}
                    <img src={CloseIcon} alt="Close" />
                </button>
            </div>

            {/* --- Add ref to chat body --- */}
            <div className="chat-body" ref={chatBodyRef}>
            {/* --- End Add ref --- */}
                {isLoading && <div className="loading-container"><ClipLoader color="#fff" size={30} /></div>}
                
                {/* Mostrar mensaje de bloqueo prominente si estoy bloqueado */}
                {amIBlocked && (
                    <div className="block-notification">
                        <div className="block-icon">🚫</div>
                        <p className="block-title">Acceso bloqueado</p>
                        <p>Has sido bloqueado por este usuario.</p>
                        <p>No puedes enviar mensajes hasta que el usuario te desbloquee.</p>
                    </div>
                )}
                
                {error && !amIBlocked && <p className="error-text">{error}</p>}
                
                {!isLoading && !error && messages.length === 0 && !amIBlocked && (
                    <p className="empty-chat-text">No messages yet. Start the conversation!</p>
                )}

                {groupedMessages.map((group, groupIndex) => (
                    // --- FIX: Modify key to include groupIndex ---
                    <React.Fragment key={`date-group-${group.date}-${groupIndex}`}>
                    {/* --- END FIX --- */}
                        <div className="date-separator">
                            <span>{formatDateSeparator(group.date)}</span>
                        </div>
                        {group.messages.map((message, messageIndex) => {
                            const isSender = message.senderAddress.toLowerCase() === currentUserAddress?.toLowerCase();
                            const showReplyContext = !!message.replyToMessage;

                            // --- Use _id (real or optimistic) as the primary key ---
                            const messageId = message._id || `msg-fallback-${group.date}-${messageIndex}`;
                            // --- End ---

                            return (
                                <div
                                    key={messageId} // Use messageId for the message container key
                                    id={`message-${messageId}`}
                                    className={`message-container ${isSender ? 'sent' : 'received'} ${highlightedMessageId === messageId ? 'message-highlighted' : ''}`}
                                >
                                    <div className="message-bubble">
                                        {/* ... Reply Context Display ... */}
                                        {showReplyContext && message.replyToMessage && (
                                            <div
                                                className="reply-context"
                                                onClick={() => handleReplyContextClick(message.replyToMessageId)}
                                                style={{ cursor: 'pointer' }}
                                                title="Go to original message"
                                            >
                                                <div className="reply-context-sender">
                                                    {message.replyToMessage.senderAddress?.toLowerCase() === currentUserAddress?.toLowerCase() ? 'You' : targetUser.username || 'User...'}
                                                </div>
                                                <div className="reply-context-content">
                                                    {message.replyToMessage.messageType === 'image'
                                                        ? '📷 Image' + (message.replyToMessage.content ? `: ${truncateText(message.replyToMessage.content, 30)}` : '')
                                                        : truncateText(message.replyToMessage.content || '', 50)} {/* Handle null/undefined content */}
                                                </div>
                                            </div>
                                        )}
                                        {/* ... Message Content (Text or Image) ... */}
                                        {message.messageType === 'image' && message.imageUrl ? (
                                            <div className="image-message-content">
                                                <img
                                                    src={apiUrl + message.imageUrl}
                                                    // ... other img props ...
                                                />
                                                {message.content && (
                                                    <p className="image-caption">{message.content}</p>
                                                )}
                                            </div>
                                        ) : (
                                            <p>{message.content}</p>
                                        )}
                                        {/* ... Message Time & Reply Button ... */}
                                        <span className="message-time">{formatMessageTime(message.timestamp)}</span>
                                        <button
                                            className="reply-button"
                                            onClick={() => handleStartReply(message)}
                                            title="Reply"
                                        >
                                            <img src={ReplyIcon} alt="Reply" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </React.Fragment>
                ))}
                <div ref={messagesEndRef} /> {/* Anchor to scroll to */}
            </div>

            {/* --- Image Preview Modal/Section --- */}
            {imagePreviewUrl && (
                <div className="image-preview-overlay">
                    <div className="image-preview-container">
                        <button onClick={handleCancelImagePreview} className="preview-close-button" title="Cancel">
                             <img src={CloseIcon} alt="Close" />
                        </button>
                        <img src={imagePreviewUrl} alt="Preview" className="image-preview-img" />
                        <div className="preview-input-area">
                            <input
                                type="text"
                                value={imageCaption}
                                onChange={handleCaptionChange}
                                onKeyPress={handleCaptionKeyPress}
                                placeholder="Add a caption... (optional)"
                                className="preview-caption-input"
                                disabled={isUploading || isSending}
                            />
                            <button
                                onClick={handleSendImageWithCaption}
                                className="preview-send-button"
                                disabled={isUploading || isSending || !selectedImageFile}
                                title="Send Image"
                            >
                                {isUploading ? <ClipLoader color="#fff" size={20} /> : <img src={SendIcon} alt="Send" />}
                            </button> {/* This is the correct closing tag */}
                        </div>
                        {uploadError && <div className="upload-error-text preview-error">{uploadError}</div>}
                    </div>
                </div>
            )}
            {/* --- End Image Preview --- */}

            {/* --- NEW: Image Viewer Modal --- */}
            {isImageViewerOpen && imageMessages.length > 0 && (
                <div className="image-viewer-overlay" onClick={closeImageViewer}>
                    {/* --- FIX: Move img inside button --- */}
                    <button className="image-viewer-close-button" onClick={closeImageViewer}>
                        <img src={CloseIcon} alt="Close" />
                    </button>
                    {/* --- END FIX --- */}
                    {imageMessages.length > 1 && (
                         <button className="image-viewer-nav-button prev" onClick={showPrevImage}>
                             <img src={ArrowLeftIcon} alt="Previous" />
                         </button>
                    )}
                    <img
                        src={apiUrl + imageMessages[imageViewerIndex].imageUrl}
                        alt="View image"
                        className="image-viewer-img"
                        onClick={(e) => e.stopPropagation()} // Prevent closing modal when clicking image
                    />
                     {/* Optionally display caption in viewer */}
                     {imageMessages[imageViewerIndex].content && (
                         <div className="image-viewer-caption" onClick={(e) => e.stopPropagation()}>
                             {imageMessages[imageViewerIndex].content}
                         </div>
                     )}
                    {imageMessages.length > 1 && (
                        <button className="image-viewer-nav-button next" onClick={showNextImage}>
                            <img src={ArrowRightIcon} alt="Next" />
                        </button>
                    )}
                </div>
            )}
           


            {/* --- Chat Footer --- */}
            <div className="chat-footer">
                 {/* --- FIX: Correct Reply Preview Structure --- */}
                 {replyingToMessage && !imagePreviewUrl && ( // Don't show reply preview if image preview is open
                    <div className="reply-preview">
                        {/* Content should be inside this div */}
                        <div className="reply-preview-content">
                            <span className="reply-preview-label"> {/* Use a label span */}
                                Replying to {replyingToMessage.senderAddress.toLowerCase() === currentUserAddress?.toLowerCase() ? 'yourself' : targetUser.username || 'User...'}:
                            </span>
                            <span className="reply-preview-text"> {/* Put truncated text inside */}
                                {replyingToMessage.messageType === 'image' ? '📷 Image' + (replyingToMessage.content ? `: ${truncateText(replyingToMessage.content, 30)}` : '') : truncateText(replyingToMessage.content || '', 40)}
                            </span>
                        </div>
                        {/* Cancel button is a sibling to reply-preview-content */}
                        <button onClick={handleCancelReply} className="cancel-reply-button" title="Cancel reply">
                            <img src={CloseSmallIcon} alt="Cancel reply" />
                        </button>
                    </div>
                 )}
                 {/* --- END FIX --- */}

                 {/* Display general sending error */}
                 {error && !uploadError && !amIBlocked && <div className="upload-error-text">{error}</div>}

                <div className="chat-input-area">
                    <button
                        onClick={triggerImageUpload}
                        className="image-upload-button"
                        disabled={!isConnected || isUploading || isSending || !!imagePreviewUrl || amIBlocked} // Disable if blocked
                        title={amIBlocked ? "Bloqueado por usuario" : "Send Image"}
                    >
                        <img src={ImageIcon} alt="Send image" />
                    </button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageFileChange}
                        accept="image/jpeg, image/png, image/gif, image/webp"
                        style={{ display: 'none' }}
                        disabled={!isConnected || isUploading || isSending || !!imagePreviewUrl || amIBlocked}
                    />
                    <input
                        type="text"
                        value={newMessage}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPress}
                        placeholder={amIBlocked ? "No puedes enviar mensajes" : "Type a message..."}
                        className={`chat-input ${amIBlocked ? 'blocked' : ''}`}
                        disabled={!isConnected || isUploading || isSending || !!imagePreviewUrl || amIBlocked} // Disable if blocked
                        readOnly={amIBlocked} // Add readOnly for extra security
                    />
                    <button
                        onClick={handleSendTextMessage}
                        className={`send-button ${amIBlocked ? 'blocked' : ''}`}
                        disabled={!isConnected || isUploading || isSending || !newMessage.trim() || !!imagePreviewUrl || amIBlocked} // Disable if blocked
                        title={amIBlocked ? "Bloqueado por usuario" : "Send Message"}
                    >
                        {isSending && !isUploading ? <ClipLoader color="#fff" size={20} /> : <img src={SendIcon} alt="Send" />}
                    </button>
                </div>
            </div>
            {/* --- End Chat Footer --- */}

             {!isConnected && <div className="connection-status">Connecting...</div>}

        </Modal> // <-- Correct: Close Modal here, after all its content
    );
};

export default ChatModal;
